"use client";

import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { canUseMerchantPermission } from "@/modules/merchant/presentation/api/client";
import { useShopBranchesListQuery } from "@/modules/merchant/presentation/api/queries";
import {
  getStoredMerchantPosBranchId,
  setStoredMerchantPosBranchId,
} from "@/modules/merchant/presentation/lib/merchant-pos-branch-storage";
import { shopBreadcrumbs } from "@/modules/merchant/presentation/lib/shop-breadcrumb";
import { useMerchantShops } from "@/modules/merchant/presentation/store/use-merchant-shops";
import { useMerchantShopsStore } from "@/modules/merchant/presentation/store/merchant-shops-store";
import { RequireMerchantPermission } from "@/modules/merchant/presentation/ui/RequireMerchantPermission";
import { MerchantPermissions } from "@/modules/roles/domain/contracts/merchant-permissions";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, Label, Loader, toast } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useCreateWorkshopJobMutation } from "../api/queries";
import { workshopToastError } from "../lib/workshop-user-messages";
import { WorkshopVehicleConfigPicker } from "../ui/WorkshopVehicleConfigPicker";

const fieldClass =
  "border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-[80px] w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export function ShopWorkshopJobNewPage() {
  const params = useParams({ strict: false });
  const shopId = params?.shopId as string | undefined;
  const nav = useNavigate();
  const createMut = useCreateWorkshopJobMutation(shopId);
  const { currentShop, currentPermissions } = useMerchantShops(shopId);
  const verifiedLevel = currentShop?.verifiedLevel ?? null;
  const shops = useMerchantShopsStore((s) => s.shops);

  const [submitting, setSubmitting] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  const membershipsForShop = useMemo(
    () => (shopId ? shops.filter((x) => x.shopId === shopId) : []),
    [shops, shopId],
  );
  const hasShopWideBranchAccess = useMemo(
    () => membershipsForShop.some((m) => m.branchId == null),
    [membershipsForShop],
  );
  const accessibleScopedBranches = useMemo(() => {
    const m = new Map<string, string>();
    for (const row of membershipsForShop) {
      if (row.branchId) {
        m.set(
          row.branchId,
          row.branchName?.trim() || `${row.branchId.slice(0, 8)}…`,
        );
      }
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [membershipsForShop]);

  const canListBranchesForPicker = canUseMerchantPermission(
    currentPermissions,
    MerchantPermissions.shop_staff.read,
    verifiedLevel,
  );
  const needBranchesFromApi =
    shopId != null && hasShopWideBranchAccess && canListBranchesForPicker;
  const branchesListQuery = useShopBranchesListQuery(
    needBranchesFromApi ? shopId : undefined,
  );

  const branchSelectOptions = useMemo(() => {
    if (!shopId) return [];
    if (hasShopWideBranchAccess && canListBranchesForPicker) {
      return (branchesListQuery.data ?? [])
        .filter((b) => b.isActive)
        .map((b) => ({
          value: b.id,
          label: b.name.trim() || `${b.id.slice(0, 8)}…`,
        }));
    }
    if (!hasShopWideBranchAccess && accessibleScopedBranches.length > 0) {
      return accessibleScopedBranches.map((b) => ({
        value: b.id,
        label: b.name,
      }));
    }
    if (currentShop?.branchId) {
      return [
        {
          value: currentShop.branchId,
          label:
            currentShop.branchName?.trim() ||
            `${currentShop.branchId.slice(0, 8)}…`,
        },
      ];
    }
    return [];
  }, [
    shopId,
    hasShopWideBranchAccess,
    canListBranchesForPicker,
    branchesListQuery.data,
    accessibleScopedBranches,
    currentShop?.branchId,
    currentShop?.branchName,
  ]);

  const branchPickerLoading =
    needBranchesFromApi &&
    branchesListQuery.isLoading &&
    branchSelectOptions.length === 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset ເມື່ອປ່ຽນຮ້ານ
  useEffect(() => {
    setSelectedBranchId("");
  }, [shopId]);

  useEffect(() => {
    if (branchSelectOptions.length === 0) {
      setSelectedBranchId("");
      return;
    }
    setSelectedBranchId((prev) => {
      if (prev && branchSelectOptions.some((o) => o.value === prev)) {
        return prev;
      }
      if (shopId) {
        const stored = getStoredMerchantPosBranchId(shopId);
        if (stored && branchSelectOptions.some((o) => o.value === stored)) {
          return stored;
        }
      }
      const fromAccount = currentShop?.branchId;
      if (
        fromAccount &&
        branchSelectOptions.some((o) => o.value === fromAccount)
      ) {
        return fromAccount;
      }
      return branchSelectOptions[0]?.value ?? "";
    });
  }, [branchSelectOptions, shopId, currentShop?.branchId]);

  useEffect(() => {
    if (!shopId || !selectedBranchId) return;
    setStoredMerchantPosBranchId(shopId, selectedBranchId);
  }, [shopId, selectedBranchId]);

  const showBranchPicker = branchSelectOptions.length > 1;

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [plateNumber, setPlateNumber] = useState("");
  const [frameNumber, setFrameNumber] = useState("");
  const [engineCode, setEngineCode] = useState("");
  const [vin, setVin] = useState("");
  const [odometer, setOdometer] = useState("");
  const [vehicleConfigurationId, setVehicleConfigurationId] = useState<
    string | null
  >(null);
  const [complaint, setComplaint] = useState("");
  const [workNotes, setWorkNotes] = useState("");
  const [vehicleConfigOpen, setVehicleConfigOpen] = useState(true);

  function validateForm(): boolean {
    if (!selectedBranchId) {
      toast.error("ກະລຸນາເລືອກສາຂາ");
      return false;
    }
    if (!customerName.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return false;
    }
    if (!customerPhone.trim()) {
      toast.error("ກະລຸນາໃສ່ເບີໂທ");
      return false;
    }
    if (!plateNumber.trim()) {
      toast.error("ກະລຸນາໃສ່ທະບຽນ");
      return false;
    }
    return true;
  }

  async function onSubmit() {
    if (!shopId || !validateForm()) return;
    setSubmitting(true);
    try {
      const job = await createMut.mutateAsync({
        branchId: selectedBranchId,
        customer: {
          name: customerName.trim(),
          phone: customerPhone.trim(),
        },
        vehicle: {
          plateNumber: plateNumber.trim(),
          frameNumber: frameNumber.trim() || null,
          engineCode: engineCode.trim() || null,
          vin: vin.trim() || null,
          vehicleConfigurationId,
          odometer:
            odometer.trim() === "" ? null : Number.parseInt(odometer, 10),
        },
        complaint: complaint.trim(),
        workNotes: workNotes.trim() || null,
      });

      toast.success(`ສ້າງໃບງານ ${job.jobNumber} ແລ້ວ`);
      await nav({
        to: "/app/shops/$shopId/workshop/jobs/$jobId",
        params: { shopId, jobId: job.id },
      });
    } catch (err) {
      toast.error(workshopToastError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const busy = submitting || createMut.isPending;

  return (
    <RequireMerchantPermission permission={MerchantPermissions.workshop.create}>
      <Header>
        <Breadcrumb items={shopBreadcrumbs.workshopJobNew(shopId)} />
      </Header>
      <Main>
        <div className="mx-auto flex max-w-lg flex-col gap-4 py-4">
          <div>
            <h1 className="font-semibold text-xl">ສ້າງໃບງານສ້ອມ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຫຼັງສ້າງໃບງານ — ເພີ່ມ/ແກ້ອາໄຫຼ່ ແລະ ຄ່າແຮງໄດ້ທຸກເວລາ (ຍົກເວັ້ນຈ່າຍແລ້ວ)
            </p>
          </div>

          {branchPickerLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader />
              ກຳລັງໂຫຼດລາຍການສາຂາ…
            </div>
          ) : branchSelectOptions.length === 0 ? (
            <p
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-950 text-sm dark:text-amber-100"
              role="alert"
            >
              {hasShopWideBranchAccess && !canListBranchesForPicker
                ? "ບັນຊີນີ້ບໍ່ມີສິດເບິ່ງລາຍການສາຂາ — ຕິດຕໍ່ຜູ້ຄຸມຮ້ານ"
                : "ບໍ່ພົບສາຂາທີ່ໃຊ້ໄດ້ — ກວດການຜູກສາຂາຂອງພະນັກງານ"}
            </p>
          ) : (
            <fieldset className="space-y-2 rounded-lg border border-border p-4">
              <legend className="px-1 font-medium text-sm">ສາຂາ</legend>
              {showBranchPicker ? (
                <>
                  <Label>ເລືອກສາຂາທີ່ຮັບງານ</Label>
                  <SimpleSelect
                    value={selectedBranchId}
                    onValueChange={setSelectedBranchId}
                    options={branchSelectOptions}
                    placeholder="ເລືອກສາຂາ"
                    className="w-full"
                  />
                  <p className="text-muted-foreground text-xs">
                    ເລືອກແລ້ວຈະຈື່ໃນຮອບນີ້ (ຮ່ວມກັບ POS)
                  </p>
                </>
              ) : (
                <p className="text-sm">
                  <span className="text-muted-foreground">ສາຂາ: </span>
                  <span className="font-medium">
                    {branchSelectOptions[0]?.label ?? "—"}
                  </span>
                </p>
              )}
            </fieldset>
          )}

          <div className="flex flex-col gap-4">
            <fieldset className="space-y-3 rounded-lg border border-border p-4">
              <legend className="px-1 font-medium text-sm">ລູກຄ້າ</legend>
              <div className="space-y-1.5">
                <Label htmlFor="cust-name">ຊື່</Label>
                <Input
                  id="cust-name"
                  required
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="ຊື່ລູກຄ້າ"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cust-phone">ເບີໂທ</Label>
                <Input
                  id="cust-phone"
                  required
                  inputMode="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="020…"
                />
              </div>
            </fieldset>

            <fieldset className="space-y-3 rounded-lg border border-border p-4">
              <legend className="px-1 font-medium text-sm">ລົດ</legend>
              <div className="space-y-1.5">
                <Label htmlFor="plate">ທະບຽນ</Label>
                <Input
                  id="plate"
                  required
                  value={plateNumber}
                  onChange={(e) => setPlateNumber(e.target.value)}
                  placeholder="ກກອ 1234"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="frame">ເລກຖັງ (ຖ້າມີ)</Label>
                  <Input
                    id="frame"
                    value={frameNumber}
                    onChange={(e) => setFrameNumber(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="engine">ລະຫັດເຄື່ອງ (ຖ້າມີ)</Label>
                  <Input
                    id="engine"
                    value={engineCode}
                    onChange={(e) => setEngineCode(e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="vin">VIN</Label>
                  <Input
                    id="vin"
                    value={vin}
                    onChange={(e) => setVin(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="odo">ໄມລ໌ (km)</Label>
                  <Input
                    id="odo"
                    inputMode="numeric"
                    value={odometer}
                    onChange={(e) => setOdometer(e.target.value)}
                  />
                </div>
              </div>
              {shopId ? (
                <div className="space-y-2">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2 text-left text-sm"
                    onClick={() => setVehicleConfigOpen((o) => !o)}
                  >
                    <span>
                      ລຸ້ນລົດ (EPC){" "}
                      {vehicleConfigurationId ? (
                        <span className="text-emerald-700 dark:text-emerald-400">
                          · ເລືອກແລ້ວ
                        </span>
                      ) : (
                        <span className="text-muted-foreground">· ແນະນຳ</span>
                      )}
                    </span>
                  </button>
                  {vehicleConfigOpen ? (
                    <WorkshopVehicleConfigPicker
                      shopId={shopId}
                      vehicleConfigurationId={vehicleConfigurationId}
                      onConfigurationChange={setVehicleConfigurationId}
                      onSaved={() => setVehicleConfigOpen(false)}
                    />
                  ) : null}
                </div>
              ) : null}
            </fieldset>

            <div className="space-y-1.5">
              <Label htmlFor="complaint">ອາການ / ວຽກທີ່ຕ້ອງເຮັດ</Label>
              <textarea
                id="complaint"
                rows={3}
                className={fieldClass}
                value={complaint}
                onChange={(e) => setComplaint(e.target.value)}
                placeholder="ເຊັ່ນ: ປ່ຽນນ້ຳມັນເຄື່ອງ"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">ໝາຍເຫດຊ່າງ</Label>
              <textarea
                id="notes"
                rows={2}
                className={fieldClass}
                value={workNotes}
                onChange={(e) => setWorkNotes(e.target.value)}
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                className="flex-1"
                disabled={busy || !selectedBranchId}
                onClick={() => void onSubmit()}
              >
                {busy ? "ກຳລັງສ້າງ…" : "ສ້າງໃບງານ"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() =>
                  shopId &&
                  void nav({
                    to: "/app/shops/$shopId/workshop",
                    params: { shopId },
                  })
                }
              >
                ຍົກເລີກ
              </Button>
            </div>
          </div>
        </div>
      </Main>
    </RequireMerchantPermission>
  );
}
